import { useMutation, useQuery, useQueryClient, UseQueryOptions } from "@tanstack/react-query";
import { useState } from "react";
import { API_ENDPOINTS } from "../../../constants/api_endpoints";
import { TResponse, TResponseWithQData } from "../../../types/common";
import { IVehicle, IVehicleCreatePayload, IVehicleUpdatePayload, IVehicleDefect, IMileageLog, IMileageLogCreatePayload } from "../../../types/api/vehicle";
import { listVehicles, vehicleDetail, createVehicle, updateVehicle, deleteVehicle, listDefects, resolveDefect, listMileageLogs, createMileageLog, listDefectChecks } from "./callbacks";
import axios from "../../../lib/axios";
import { IVehicleDailyCheck } from "../../../types/api/incident";

export const useVehicleList = (initialParams: any) => {
  const [params, setParams] = useState(initialParams);
  const result = useQuery({
    queryKey: [API_ENDPOINTS.VEHICLE_LIST, { ...params, total: null }],
    queryFn: () => listVehicles(params).then((r) => r.data),
    select: (res: any) => ({
      ...res,
      data: (res.data || []).map((x: IVehicle) => ({ key: x.id, ...x })),
      qData: { ...params, total: res.extra?.total },
    }),
    initialData: { data: [], success: true, message: "", code: 200, qData: { page: 1, limit: 20 }, extra: { page: 1, limit: 20, total: 0 } },
    staleTime: 20000,
    initialDataUpdatedAt: 0,
    refetchOnWindowFocus: false,
  });
  return { ...result, refetchWithQuery: (p: any) => setParams((prev: any) => ({ ...prev, ...p })) };
};

export const useVehicleDetail = (id: number) =>
  useQuery({
    queryKey: [API_ENDPOINTS.VEHICLE_DETAIL, id],
    queryFn: () => vehicleDetail(id).then((r) => r.data),
    enabled: !!id,
  });

export const useVehicleCreate = (options?: { onSuccess?: (d: IVehicle) => void; onError?: (e: any) => void }) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: IVehicleCreatePayload) => createVehicle(data).then((r) => r.data),
    onSuccess: (res) => { qc.invalidateQueries({ queryKey: [API_ENDPOINTS.VEHICLE_LIST] }); options?.onSuccess?.(res.data as IVehicle); },
    onError: options?.onError,
  });
};

export const useVehicleUpdate = (options?: { onSuccess?: (d: IVehicle) => void; onError?: (e: any) => void }) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: IVehicleUpdatePayload) => updateVehicle(data).then((r) => r.data),
    onSuccess: (res) => { qc.invalidateQueries({ queryKey: [API_ENDPOINTS.VEHICLE_LIST] }); options?.onSuccess?.(res.data as IVehicle); },
    onError: options?.onError,
  });
};

export const useVehicleDelete = (options?: { onSuccess?: () => void; onError?: (e: any) => void }) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteVehicle(id).then((r) => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: [API_ENDPOINTS.VEHICLE_LIST] }); options?.onSuccess?.(); },
    onError: options?.onError,
  });
};

export const useDefectChecks = (vehicle_id: number) =>
  useQuery({
    queryKey: [API_ENDPOINTS.VEHICLE_DEFECT_CHECKS, vehicle_id],
    queryFn: () => listDefectChecks(vehicle_id).then((r) => r.data),
    enabled: !!vehicle_id,
  });

export const useDefectList = (initialParams: any) => {
  const [params, setParams] = useState(initialParams);
  const result = useQuery({
    queryKey: [API_ENDPOINTS.VEHICLE_DEFECTS_LIST, { ...params, total: null }],
    queryFn: () => listDefects(params).then((r) => r.data),
    select: (res: any) => ({ ...res, data: (res.data || []).map((x: IVehicleDefect) => ({ key: x.id, ...x })) }),
    initialData: { data: [], success: true, message: "", code: 200 },
    staleTime: 20000,
    initialDataUpdatedAt: 0,
    refetchOnWindowFocus: false,
  });
  return { ...result, refetchWithQuery: (p: any) => setParams((prev: any) => ({ ...prev, ...p })) };
};

export const useResolveDefect = (options?: { onSuccess?: () => void }) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => resolveDefect(id).then((r) => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: [API_ENDPOINTS.VEHICLE_DEFECTS_LIST] }); options?.onSuccess?.(); },
  });
};

export const useMileageList = (initialParams: any) => {
  const [params, setParams] = useState(initialParams);
  const result = useQuery({
    queryKey: [API_ENDPOINTS.VEHICLE_MILEAGE_LIST, { ...params, total: null }],
    queryFn: () => listMileageLogs(params).then((r) => r.data),
    select: (res: any) => ({
      ...res,
      data: (res.data || []).map((x: IMileageLog) => ({ key: x.id, ...x })),
      qData: { ...params, total: res.extra?.total },
    }),
    initialData: { data: [], success: true, message: "", code: 200, qData: { page: 1, limit: 20 }, extra: { page: 1, limit: 20, total: 0 } },
    staleTime: 20000,
    initialDataUpdatedAt: 0,
    refetchOnWindowFocus: false,
  });
  return { ...result, refetchWithQuery: (p: any) => setParams((prev: any) => ({ ...prev, ...p })) };
};

export const useMileageLogCreate = (options?: { onSuccess?: (d: IMileageLog) => void; onError?: (e: any) => void }) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: IMileageLogCreatePayload) => createMileageLog(data).then((r) => r.data),
    onSuccess: (res) => { qc.invalidateQueries({ queryKey: [API_ENDPOINTS.VEHICLE_MILEAGE_LIST] }); options?.onSuccess?.(res.data as IMileageLog); },
    onError: options?.onError,
  });
};

export const useVehicleDailyChecksList = (initialParams: any) => {
  const [params, setParams] = useState(initialParams);
  const result = useQuery({
    queryKey: [API_ENDPOINTS.VEHICLE_DAILY_CHECKS_LIST, { ...params, total: null }],
    queryFn: async () => {
      const res = await axios.get(API_ENDPOINTS.VEHICLE_DAILY_CHECKS_LIST, { params });
      return res.data;
    },
    select: (res: any) => ({
      ...res,
      data: (res.data || []).map((x: IVehicleDailyCheck) => ({ key: x.id, ...x })),
      qData: { ...params, total: res.extra?.total },
    }),
    initialData: { data: [], success: true, message: "", code: 200, qData: {}, extra: { page: 1, limit: 20, total: 0 } },
    staleTime: 20000,
    initialDataUpdatedAt: 0,
    refetchOnWindowFocus: false,
  });
  return { ...result, refetchWithQuery: (p: any) => setParams((prev: any) => ({ ...prev, ...p })) };
};
